import {
  useId,
  useMemo,
} from 'react'
import {
  useTranslation,
} from 'react-i18next'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import moment from 'moment'
import {
  ExpandMore,
} from '@vedmarket/ui-kit'

import {
  APPLICATION_EVENT_TYPE,
} from 'config/constants'
import {
  formatReferenceCopy,
} from 'utils/typography'

import {
  proposalDeclinedEventReason,
  proposalHistoryEvents,
  proposalPublishedEventLines,
} from '../proposal-history-utils'

const ManagerProposalHistory = ({ events }: ApplicationManagerProposalHistoryProps) => {
  const { t } = useTranslation()
  const historyId = useId()
  const items = useMemo(() => proposalHistoryEvents(events), [ events ])

  if (!items.length) {
    return null
  }

  return (
    <Accordion className='application-manager-proposal-history'>
      <AccordionSummary
        expandIcon={<ExpandMore />}
        id={`${historyId}-summary`}
        aria-controls={`${historyId}-content`}
        sx={{
          paddingLeft: 0,
          paddingRight: 0,
        }}
      >
        <Typography variant='body1' color='text.primary'>
          {formatReferenceCopy(t('applications__text__proposalHistory'))} ({items.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails id={`${historyId}-content`} sx={{
        paddingLeft: 0,
        paddingRight: 0,
      }}>
        <Stack spacing={2} divider={<Divider />}>
          {items.map((event) => {
            const publishedLines = proposalPublishedEventLines(event, t)
            const declineReason = proposalDeclinedEventReason(event)
            const showPublishedLines = event.event_type === APPLICATION_EVENT_TYPE.PROPOSAL_PUBLISHED
              && publishedLines.length > 0

            return (
              <Stack key={event.id} component='article' spacing={1.5}>
                <Stack spacing={0.5}>
                  <Typography
                    variant='subtitle2'
                    component='h3'
                    color='text.primary'
                  >
                    {event.event_type_label}
                  </Typography>
                  <Typography variant='caption' component='p' color='text.primary'>
                    {moment(event.created_at).format('DD.MM.YYYY HH:mm')}
                    {event.actor_name ? ` · ${event.actor_name}` : ''}
                  </Typography>
                </Stack>
                {showPublishedLines && (
                  <Stack spacing={1.5}>
                    {publishedLines.map((line) => (
                      <Stack key={line.key} spacing={0.5}>
                        <Typography
                          variant={line.key === 'total' ? 'subtitle2' : 'body2'}
                          color='text.primary'
                          sx={{
                            overflowWrap: 'anywhere',
                          }}
                        >
                          {formatReferenceCopy(line.text)}
                        </Typography>
                        {line.hint && (
                          <Typography variant='caption' component='p' color='text.primary'>
                            {formatReferenceCopy(line.hint)}
                          </Typography>
                        )}
                        {line.comment && (
                          <Typography
                            variant='body2'
                            color='text.primary'
                            sx={{
                              whiteSpace: 'pre-wrap',
                              overflowWrap: 'anywhere',
                            }}
                          >
                            {line.comment}
                          </Typography>
                        )}
                      </Stack>
                    ))}
                  </Stack>
                )}
                {declineReason && (
                  <Typography
                    variant='body2'
                    color='text.primary'
                    sx={{
                      whiteSpace: 'pre-wrap',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {formatReferenceCopy(t('applications__text__proposalDeclineReasonLabel'))}: {declineReason}
                  </Typography>
                )}
              </Stack>
            )
          })}
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}

export default ManagerProposalHistory
